import { ReloadOutlined } from '@ant-design/icons';
import type { OrderCenterDetail, OrderCenterRow, SplitRequestRow, SupplyTriggerType } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Drawer,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { hq } from '../session';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const TRIGGER_LABEL: Record<string, { text: string; color: string }> = {
  STOCK_OUT: { text: '本院库出库', color: 'green' },
  FORCE_OUTER: { text: '强制外仓', color: 'blue' },
  EMERGENCY: { text: '应急外仓', color: 'red' },
  SUPPLIER_SYNC: { text: '供应商同步', color: 'orange' }
};

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  NEW: { text: '待接单', color: 'red' },
  ACCEPTED: { text: '已接单', color: 'blue' },
  PICKING: { text: '拣货中', color: 'gold' },
  PACKED: { text: '已打包', color: 'cyan' },
  SHIPPED: { text: '已发货', color: 'geekblue' },
  DELIVERING: { text: '配送中', color: 'purple' },
  SIGNED: { text: '已签收', color: 'green' },
  CANCELED: { text: '已拒单', color: 'default' }
};

/** 接单中心：汇聚各院外仓供货指令 + 判源结果，支持接单/拒单（演-1 一体化）。 */
export default function OrderCenter() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('NEW');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [splitIds, setSplitIds] = useState<string[]>([]);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitRemark, setSplitRemark] = useState('');
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  // 拆单审核：提交后落 wl_split_request（REVIEW），审核通过才生成子单
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rejectingSplit, setRejectingSplit] = useState<SplitRequestRow | null>(null);
  const [splitOpinion, setSplitOpinion] = useState('');

  // 游标分页：后端 size 上限 200、超出回落 20，按 size 递增翻页会在第 11 次突然缩到 20 条；
  // 改为 lastId 游标 + 前端累加，每次固定 20 条
  const list = useInfiniteQuery({
    queryKey: ['order-center', status],
    queryFn: ({ pageParam }) =>
      hq.orderCenter.pending({ supplyStatus: status || undefined, size: PAGE_SIZE, lastId: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextLastId : null)
  });
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const detail = useQuery<OrderCenterDetail>({
    queryKey: ['order-center', 'detail', detailId],
    queryFn: () => hq.orderCenter.detail(detailId ?? ''),
    enabled: Boolean(detailId)
  });

  const split = useMutation({
    mutationFn: () =>
      hq.opsExtras.wave.split(detailId ?? '', {
        itemIds: splitIds,
        remark: splitRemark.trim() || undefined
      }),
    onSuccess: async (result) => {
      message.success(`已提交拆单审核 ${result.bizNo}，审核通过后生成子单`);
      setSplitOpen(false);
      setSplitIds([]);
      setSplitRemark('');
      await queryClient.invalidateQueries({ queryKey: ['order-center'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '提交拆单申请失败')
  });

  const splitReviews = useQuery({
    queryKey: ['order-center', 'split-requests'],
    queryFn: () => hq.opsExtras.splitRequests.list()
  });

  const approveSplit = useMutation({
    mutationFn: (id: string) => hq.opsExtras.splitRequests.approve(id),
    onSuccess: async () => {
      message.success('已审核通过，子单已生成');
      await queryClient.invalidateQueries({ queryKey: ['order-center'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '审核失败')
  });

  const rejectSplit = useMutation({
    mutationFn: (row: SplitRequestRow) => hq.opsExtras.splitRequests.reject(row.id, splitOpinion.trim() || undefined),
    onSuccess: async () => {
      message.success('已驳回拆单申请');
      setRejectingSplit(null);
      setSplitOpinion('');
      await queryClient.invalidateQueries({ queryKey: ['order-center'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '驳回失败')
  });

  // 判源结果汇总：接单前先看「本院库出库 / 外仓直通 / 应急」的构成，避免仅凭列表逐单判断
  const all = useQuery({
    queryKey: ['order-center', 'all'],
    queryFn: () => hq.orderCenter.pending({ size: 200 }),
    staleTime: 60 * 1000
  });

  const summary = useMemo(() => {
    const rows = all.data?.items ?? [];
    const byTrigger = rows.reduce<Record<string, number>>((acc, row) => {
      acc[row.triggerType] = (acc[row.triggerType] ?? 0) + 1;
      return acc;
    }, {});
    return {
      total: all.data?.items.length ?? 0,
      pending: rows.filter((row) => row.supplyStatus === 'NEW').length,
      emergency: rows.filter((row) => row.isEmergency).length,
      overtime: rows.filter((row) => row.isOvertime).length,
      byTrigger
    };
  }, [all.data]);

  const accept = useMutation({
    mutationFn: (id: string) => hq.orderCenter.accept(id),
    onSuccess: async () => {
      message.success('已接单');
      await queryClient.invalidateQueries({ queryKey: ['order-center'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '接单失败')
  });

  const reject = useMutation({
    mutationFn: (id: string) => hq.orderCenter.reject(id, reason || undefined),
    onSuccess: async () => {
      message.success('已拒单');
      setRejecting(null);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['order-center'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '拒单失败')
  });

  const columns: ColumnsType<OrderCenterRow> = [
    {
      title: '供货单号',
      dataIndex: 'bizNo',
      width: 200,
      render: (value: string, row) => <Typography.Link onClick={() => setDetailId(row.id)}>{value}</Typography.Link>
    },
    { title: '需求机构', dataIndex: 'orgName', width: 180, render: (v: string | null) => v || '-' },
    { title: '需求科室', dataIndex: 'demandDeptName', width: 140, render: (v: string | null) => v || '-' },
    { title: '承接外仓', dataIndex: 'outerWarehouseName', width: 160, render: (v: string | null) => v || '-' },
    {
      title: '判源结果',
      dataIndex: 'triggerType',
      width: 130,
      render: (value: SupplyTriggerType) => {
        const meta = TRIGGER_LABEL[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'supplyStatus',
      width: 100,
      render: (value: string) => {
        const meta = STATUS_LABEL[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (v: number) => formatAmount(v) },
    {
      title: '应急 / 超时',
      width: 120,
      render: (_, row) => (
        <Space size={4}>
          {row.isEmergency ? <Tag color="red">应急</Tag> : null}
          {row.isOvertime ? <Tag color="orange">超时</Tag> : null}
        </Space>
      )
    },
    { title: '通知时间', dataIndex: 'notifyAt', width: 160, render: (v: string | null) => formatDisplayTime(v) },
    {
      title: '操作',
      width: 140,
      render: (_, row) =>
        row.supplyStatus === 'NEW' ? (
          <Space size={4}>
            <Button size="small" type="link" loading={accept.isPending} onClick={() => accept.mutate(row.id)}>
              接单
            </Button>
            <Button size="small" type="link" danger onClick={() => setRejecting(row.id)}>
              拒单
            </Button>
          </Space>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        )
    }
  ];

  return (
    <Card
      className="page-card"
      title="接单中心"
      extra={
        <Space>
          <Select
            value={status}
            style={{ width: 130 }}
            onChange={setStatus}
            options={[
              { value: 'NEW', label: '待接单' },
              { value: 'ACCEPTED', label: '已接单' },
              { value: 'PICKING', label: '拣货中' },
              { value: 'PACKED', label: '已打包' },
              { value: 'SHIPPED', label: '已发货' },
              { value: 'DELIVERING', label: '配送中' },
              { value: 'SIGNED', label: '已签收' },
              { value: 'CANCELED', label: '已拒单' },
              { value: 'ALL', label: '全部' }
            ]}
          />
          <Button onClick={() => setReviewOpen(true)}>
            拆单审核{splitReviews.data?.length ? `（${splitReviews.data.length}）` : ''}
          </Button>
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => list.refetch()}>
            刷新
          </Button>
        </Space>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="待接单" value={summary.pending} suffix={`/ ${summary.total}`} loading={all.isPending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="应急外仓"
              value={summary.emergency}
              suffix="单"
              loading={all.isPending}
              valueStyle={{ color: summary.emergency > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="超时"
              value={summary.overtime}
              suffix="单"
              loading={all.isPending}
              valueStyle={{ color: summary.overtime > 0 ? '#d46b08' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" title="判源构成" styles={{ header: { minHeight: 36, paddingBlock: 4 } }}>
            <Space wrap size={4}>
              {Object.entries(summary.byTrigger).map(([trigger, count]) => (
                <Tag key={trigger} color={TRIGGER_LABEL[trigger]?.color ?? 'default'}>
                  {TRIGGER_LABEL[trigger]?.text ?? trigger} {count}
                </Tag>
              ))}
              {Object.keys(summary.byTrigger).length === 0 ? <Typography.Text type="secondary">-</Typography.Text> : null}
            </Space>
            {/* 汇总与列表是两条查询：列表走游标翻页，汇总只取一页，故必须显式标注口径 */}
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {all.data?.hasMore ? '口径：最近 200 单（超出部分未计入）' : '口径：全部待处理单'}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="接单列表加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<OrderCenterRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无外仓供货指令' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 单</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.order.bizNo ?? '供货指令详情'}
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        width={860}
      >
        {detail.isLoading ? null : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Space direction="vertical" style={{ marginBottom: 16 }}>
              <Typography.Text>需求机构：{detail.data.order.orgName ?? '-'}</Typography.Text>
              <Typography.Text>需求科室：{detail.data.order.demandDeptName ?? '-'}</Typography.Text>
              <Typography.Text>承接外仓：{detail.data.order.outerWarehouseName ?? '-'}</Typography.Text>
              <Typography.Text>应到时间：{formatDisplayTime(detail.data.order.slaDeadline)}</Typography.Text>
              <Typography.Text>关联配送单：{detail.data.order.deliveryId || '未生成'}</Typography.Text>
            </Space>

            {['NEW', 'ACCEPTED'].includes(detail.data.order.supplyStatus) ? (
              <Space style={{ marginBottom: 8 }}>
                <Button
                  size="small"
                  disabled={splitIds.length === 0 || splitIds.length === detail.data.items.length}
                  onClick={() => setSplitOpen(true)}
                >
                  拆出子单（{splitIds.length} 行）
                </Button>
                <Typography.Text type="secondary">按行拆单用于分仓/分批发运，子单沿用原判源与 SLA</Typography.Text>
              </Space>
            ) : null}

            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.data.items}
              rowSelection={
                ['NEW', 'ACCEPTED'].includes(detail.data.order.supplyStatus)
                  ? { selectedRowKeys: splitIds, onChange: (keys) => setSplitIds(keys as string[]) }
                  : undefined
              }
              columns={[
                { title: '行号', dataIndex: 'lineNo', width: 70 },
                { title: '物资编码', dataIndex: 'materialCode', width: 130, render: (v: string | null) => v || '-' },
                { title: '物资名称', dataIndex: 'materialName', render: (v: string | null) => v || '-' },
                { title: '规格', dataIndex: 'spec', width: 130, render: (v: string | null) => v || '-' },
                { title: '数量', dataIndex: 'qty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (v: number) => formatAmount(v) }
              ]}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="提交拆单申请"
        open={splitOpen}
        onCancel={() => setSplitOpen(false)}
        onOk={() => split.mutate()}
        confirmLoading={split.isPending}
        okText="提交审核"
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          将选中 {splitIds.length} 行提交拆单审核，原单保留其余行；审核通过后才生成 GY 子单（子单需单独接单/发运）。
        </Typography.Paragraph>
        <Input.TextArea rows={2} placeholder="拆单原因（可选）" value={splitRemark} onChange={(event) => setSplitRemark(event.target.value)} maxLength={120} />
      </Modal>

      <Drawer title="拆单审核" open={reviewOpen} onClose={() => setReviewOpen(false)} width={860}>
        {splitReviews.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="拆单申请加载失败"
            description={splitReviews.error instanceof ApiError ? splitReviews.error.friendlyMessage : String(splitReviews.error)}
          />
        ) : null}
        <Table<SplitRequestRow>
          rowKey="id"
          size="small"
          dataSource={splitReviews.data ?? []}
          loading={splitReviews.isPending}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: '没有待审核的拆单申请' }}
          columns={[
            { title: '申请单号', dataIndex: 'bizNo', width: 190 },
            { title: '原指令', dataIndex: 'parentBizNo', width: 190 },
            { title: '拆出行数', dataIndex: 'itemCount', width: 100, align: 'right' },
            { title: '拆单原因', dataIndex: 'remark', render: (value: string | null) => value || '-' },
            {
              title: '申请人',
              width: 120,
              render: (_, row) => row.submitByName ?? row.submitBy
            },
            { title: '提交时间', dataIndex: 'submitAt', width: 160, render: (value: string) => formatDisplayTime(value) },
            {
              title: '原单状态',
              dataIndex: 'parentSupplyStatus',
              width: 100,
              render: (value: string | null) => {
                const meta = value ? STATUS_LABEL[value] : undefined;
                return meta ? <Tag color={meta.color}>{meta.text}</Tag> : value || '-';
              }
            },
            {
              title: '审核状态',
              dataIndex: 'status',
              width: 100,
              render: (value: string) => (
                <Tag color={value === 'APPROVED' ? 'green' : value === 'REJECTED' ? 'default' : 'gold'}>
                  {({ REVIEW: '待审核', APPROVED: '已通过', REJECTED: '已驳回' } as Record<string, string>)[value] ?? value}
                </Tag>
              )
            },
            {
              title: '操作',
              width: 150,
              render: (_, row) =>
                row.status === 'REVIEW' ? (
                  <Space size={4}>
                    <Popconfirm
                      title="审核通过后将生成子单并移动明细，确认？"
                      okText="通过"
                      onConfirm={() => approveSplit.mutate(row.id)}
                    >
                      <Button size="small" type="link" loading={approveSplit.isPending}>
                        通过
                      </Button>
                    </Popconfirm>
                    <Button size="small" type="link" danger onClick={() => setRejectingSplit(row)}>
                      驳回
                    </Button>
                  </Space>
                ) : (
                  <Typography.Text type="secondary">{row.childBizNo ?? '-'}</Typography.Text>
                )
            }
          ]}
        />
      </Drawer>

      <Modal
        title="驳回拆单申请"
        open={rejectingSplit !== null}
        onCancel={() => {
          setRejectingSplit(null);
          setSplitOpinion('');
        }}
        onOk={() => rejectingSplit && rejectSplit.mutate(rejectingSplit)}
        confirmLoading={rejectSplit.isPending}
        okText="确认驳回"
        okButtonProps={{ danger: true }}
      >
        <Typography.Paragraph type="secondary">
          驳回后原单不受影响；如需继续拆分，请重新提交申请。
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          placeholder="驳回原因（可选）"
          value={splitOpinion}
          onChange={(event) => setSplitOpinion(event.target.value)}
          maxLength={200}
        />
      </Modal>

      <Modal
        title="拒单"
        open={rejecting !== null}
        onCancel={() => setRejecting(null)}
        onOk={() => rejecting && reject.mutate(rejecting)}
        confirmLoading={reject.isPending}
        okText="确认拒单"
        okButtonProps={{ danger: true }}
      >
        <Typography.Paragraph type="secondary">
          拒单后该供货指令作废，需由需求机构重新提交或由运营人工改判。
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          placeholder="拒单原因（如：外仓缺货）"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </Card>
  );
}
